import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { quien } from './comercialUtiles'

// What the organization's and the contact's cards share: a section, a label
// with its value, the "⋯" menu, the Registro tab and the notice after an action.

export function Seccion({ titulo, children }) {
  return (
    <section>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{titulo}</h3>
      <div className="space-y-2">{children}</div>
    </section>
  )
}

// A label and its value; "—" when empty, so what is missing shows.
export function Dato({ etiqueta, children }) {
  const vacio = children === null || children === undefined || children === '' || children === false
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-3 text-sm">
      <span className="text-gray-500">{etiqueta}</span>
      <span className={vacio ? 'text-gray-300' : 'text-gray-800 break-words'}>{vacio ? '—' : children}</span>
    </div>
  )
}

// Who created it, last changed it and archived it, and when: what the API keeps
// (no history of changes). Restoring clears the archiving, so it shows only while archived.
//   archivo: { si, por, en, etiqueta } -- the record's own archived fields
export function Registro({ r, archivo, etiquetaCreacion }) {
  const { t } = useTranslation('comercial')
  return (
    <div className="space-y-2">
      <Dato etiqueta={etiquetaCreacion}>{quien(t, r.creado_por, r.creado_en)}</Dato>
      <Dato etiqueta={t('ficha.modificada')}>{r.actualizado_en && quien(t, r.actualizado_por, r.actualizado_en)}</Dato>
      {archivo.si && <Dato etiqueta={archivo.etiqueta}>{quien(t, archivo.por, archivo.en)}</Dato>}
    </div>
  )
}

// "⋯": the less frequent actions (archive / restore, delete). A disabled option says why.
export function MenuMas({ opciones, disabled }) {
  const { t } = useTranslation('comercial')
  const [abierto, setAbierto] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!abierto) return undefined
    const fuera = (e) => { if (!ref.current?.contains(e.target)) setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [abierto])
  if (!opciones.length) return null
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setAbierto(a => !a)} disabled={disabled} aria-haspopup="menu" aria-expanded={abierto}
        title={t('ficha.mas')} aria-label={t('ficha.mas')}
        className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40 leading-none">⋯</button>
      {abierto && (
        <div role="menu" className="absolute right-0 top-full mt-1 z-10 w-56 rounded-lg border border-gray-200 bg-white shadow-lg py-1">
          {opciones.map(op => (
            <button key={op.texto} type="button" role="menuitem" disabled={!!op.deshabilitada} title={op.deshabilitada || undefined}
              onClick={() => { setAbierto(false); op.onClick() }}
              className={`block w-full text-left px-3 py-2 text-sm disabled:cursor-not-allowed ${op.peligro ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'} disabled:text-gray-300 disabled:hover:bg-transparent`}>
              {op.texto}
              {op.deshabilitada && <span className="block text-xs text-gray-400">{op.deshabilitada}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// The amber notice at the top of a card after an action that couldn't be done,
// with the organizations it names (each opens its card).
//   aviso: { texto, orgs?: [{ id, nombre_comercial }] }
export function AvisoFicha({ aviso, onCerrar, abrir }) {
  const { t } = useTranslation('comercial')
  if (!aviso) return null
  return (
    <div role="alert" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <div className="flex items-start justify-between gap-2">
        <p>{aviso.texto}</p>
        <button type="button" onClick={onCerrar} aria-label={t('panel.cerrar')}
          className="shrink-0 text-amber-700 hover:text-amber-900 leading-none">×</button>
      </div>
      {aviso.orgs?.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {aviso.orgs.map(x => (
            <li key={x.id}>
              <button type="button" onClick={() => abrir('org', x.id)} className="underline hover:text-amber-950">{x.nombre_comercial}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
